import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { MapPin, Plus, Pencil, Trash2, Star, Building2 } from 'lucide-react';
import { useVendorLocations, VendorLocationInput } from '@/hooks/useVendorLocations';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { AddressValidationBadge } from '@/components/shared/AddressValidationBadge';
import { AddressSuggestionDialog } from '@/components/shared/AddressSuggestionDialog';
import { addressDiffersFromStandardized } from '@/utils/addressUtils';

interface VendorLocationsTabProps {
  userId: string;
  canEdit: boolean;
}

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }, { value: 'DC', label: 'District of Columbia' },
];

interface FormData extends VendorLocationInput {
  address_validated?: boolean;
  address_deliverability?: string;
}

const emptyFormData: FormData = {
  location_name: '',
  address_text: '',
  city: '',
  state: '',
  country: 'US',
  postal_code: '',
  is_primary: false,
  address_validated: false,
  address_deliverability: undefined,
};

export function VendorLocationsTab({ userId, canEdit }: VendorLocationsTabProps) {
  const { locations, loading, createLocation, updateLocation, deleteLocation, setPrimary } = useVendorLocations(userId);
  const { validateAddress, isValidating } = useAddressValidation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({ ...emptyFormData, is_primary: locations.length === 0 });
    setValidationResult(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (location: typeof locations[0]) => {
    setEditingId(location.id);
    setFormData({
      location_name: location.location_name || '',
      address_text: location.address_text || '',
      city: location.city || '',
      state: location.state || '',
      country: location.country || 'US',
      postal_code: location.postal_code || '',
      is_primary: location.is_primary,
      address_validated: location.address_validated || false,
      address_deliverability: location.address_deliverability || undefined,
    });
    setValidationResult(null);
    setDialogOpen(true);
  };

  const handleValidateAndSave = async () => {
    if (!formData.location_name || !formData.address_text || !formData.city || !formData.state || !formData.postal_code) {
      return;
    }

    // Validate the address first
    const result = await validateAddress({
      street: formData.address_text,
      city: formData.city,
      state: formData.state,
      zipcode: formData.postal_code,
    });

    // Check if standardized address differs from user input
    const addressDiffers = addressDiffersFromStandardized(
      {
        street: formData.address_text,
        city: formData.city,
        state: formData.state,
        zipcode: formData.postal_code,
      },
      result.standardizedAddress
    );

    const hasSuggestions = result.standardizedAddress || 
      (result.suggestions && result.suggestions.length > 0);

    // Show dialog if address was corrected OR if invalid with suggestions
    if (addressDiffers || (hasSuggestions && !result.isValid)) {
      setValidationResult(result);
      setSuggestionDialogOpen(true);
    } else if (result.isValid) {
      // Address is valid and matches - save directly
      await saveLocation({
        ...formData,
        address_validated: true,
        address_deliverability: result.deliverability,
      });
    } else {
      // No valid address and no suggestions - show dialog anyway
      setValidationResult(result);
      setSuggestionDialogOpen(true);
    }
  };

  const saveLocation = async (data: FormData) => {
    setSaving(true);
    try {
      if (editingId) {
        await updateLocation(editingId, data);
      } else {
        await createLocation(data);
      }
      setDialogOpen(false);
      setFormData(emptyFormData);
      setValidationResult(null);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectSuggestedAddress = async (suggestion: any) => {
    const updatedData = {
      ...formData,
      address_text: suggestion.street,
      city: suggestion.city,
      state: suggestion.state,
      postal_code: suggestion.zipcode,
      address_validated: true,
      address_validated_at: new Date().toISOString(),
      address_deliverability: validationResult?.deliverability || 'unknown',
    };
    setSuggestionDialogOpen(false);
    await saveLocation(updatedData);
  };

  const handleKeepOriginal = async () => {
    setSuggestionDialogOpen(false);
    await saveLocation({
      ...formData,
      address_validated: false,
      address_validated_at: new Date().toISOString(),
      address_deliverability: 'unverified',
    });
  };

  const handleDelete = async (locationId: string) => {
    await deleteLocation(locationId);
  };

  const handleSetPrimary = async (locationId: string) => {
    await setPrimary(locationId);
  };

  const getValidationStatus = (location: any) => {
    if (location.address_validated) {
      return {
        isValidated: true,
        deliverability: location.address_deliverability || 'deliverable',
      };
    }
    return {
      isValidated: false,
      deliverability: undefined,
    };
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Manufacturing Locations
            </CardTitle>
            <CardDescription>
              Register your plant and facility locations. These are used when submitting bids to indicate where items will be produced.
            </CardDescription>
          </div>
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleOpenCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Edit Location' : 'Add New Location'}</DialogTitle>
                  <DialogDescription>
                    Enter your manufacturing plant or facility details. Address will be validated via USPS.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="location_name">Location Name *</Label>
                    <Input
                      id="location_name"
                      value={formData.location_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, location_name: e.target.value }))}
                      placeholder="e.g., Chicago Plant, Dallas Distribution Center"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={formData.address_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_text: e.target.value, address_validated: false }))}
                      placeholder="123 Industrial Blvd"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value, address_validated: false }))}
                        placeholder="Chicago"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Select
                        value={formData.state}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, state: v, address_validated: false }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {US_STATES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postal Code *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value, address_validated: false }))}
                        placeholder="60601"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                        placeholder="US"
                        disabled
                      />
                    </div>
                  </div>
                  {formData.address_validated && (
                    <div className="flex items-center gap-2">
                      <AddressValidationBadge
                        isValidated={true}
                        deliverability={formData.address_deliverability as any}
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleValidateAndSave} disabled={saving || isValidating}>
                    {isValidating ? 'Validating...' : saving ? 'Saving...' : editingId ? 'Validate & Save' : 'Validate & Add'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && locations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Loading locations...</div>
        ) : locations.length === 0 ? (
          <div className="text-center py-8">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Locations Registered</h3>
            <p className="text-muted-foreground mb-4">
              Add your manufacturing plants and facilities to use them when submitting bids.
            </p>
            {canEdit && (
              <Button onClick={handleOpenCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Location
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>City, State</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((location) => {
                const validationStatus = getValidationStatus(location);
                return (
                  <TableRow key={location.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {location.location_name || 'Unnamed Location'}
                        {location.is_primary && (
                          <Badge variant="default" className="text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {location.address_text || '-'}
                    </TableCell>
                    <TableCell>
                      {location.city}, {location.state} {location.postal_code}
                    </TableCell>
                    <TableCell>
                      <AddressValidationBadge
                        isValidated={validationStatus.isValidated}
                        deliverability={validationStatus.deliverability}
                        compact
                      />
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!location.is_primary && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetPrimary(location.id)}
                              title="Set as primary"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(location)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={location.is_primary && locations.length > 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Location</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{location.location_name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(location.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Address Suggestion Dialog - only render when validationResult exists */}
      {validationResult && (
        <AddressSuggestionDialog
          open={suggestionDialogOpen}
          onOpenChange={setSuggestionDialogOpen}
          originalAddress={{
            street: formData.address_text,
            city: formData.city,
            state: formData.state,
            zipcode: formData.postal_code,
          }}
          validationResult={validationResult}
          onSelectAddress={handleSelectSuggestedAddress}
          onKeepOriginal={handleKeepOriginal}
        />
      )}
    </Card>
  );
}
